import { useSyncExternalStore, useCallback } from 'react';
import { parseRoute, buildPath } from '../lib/routes';
import type { InvestmentsTabId, Route, SectionId } from '../lib/routes';

/**
 * Store mínimo em cima da History API. Usar useSyncExternalStore mantém todos
 * os consumidores com o mesmo caminho — sem isso, a aba de investimentos e o
 * dashboard teriam cópias independentes que saem de sincronia.
 */

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', emit);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): string {
  return typeof window === 'undefined' ? '/' : window.location.pathname;
}

export function navigateTo(path: string, { replace = false } = {}): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === path) return;

  const url = `${path}${window.location.search}${window.location.hash}`;
  if (replace) {
    window.history.replaceState(null, '', url);
  } else {
    window.history.pushState(null, '', url);
  }
  emit();
}

export interface UseRouteResult extends Route {
  /** Troca de aba empilha no histórico, então o botão voltar funciona. */
  goToSection: (section: SectionId, investmentsTab?: InvestmentsTabId) => void;
  goToInvestmentsTab: (tab: InvestmentsTabId) => void;
  /** Conserta a URL sem criar entrada no histórico. */
  replaceWith: (section: SectionId, investmentsTab?: InvestmentsTabId) => void;
}

export function useRoute(): UseRouteResult {
  const pathname = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const route = parseRoute(pathname);

  const goToSection = useCallback(
    (section: SectionId, investmentsTab?: InvestmentsTabId) => {
      navigateTo(buildPath(section, investmentsTab));
    },
    []
  );

  const goToInvestmentsTab = useCallback((tab: InvestmentsTabId) => {
    navigateTo(buildPath('investments', tab));
  }, []);

  const replaceWith = useCallback(
    (section: SectionId, investmentsTab?: InvestmentsTabId) => {
      navigateTo(buildPath(section, investmentsTab), { replace: true });
    },
    []
  );

  return { ...route, goToSection, goToInvestmentsTab, replaceWith };
}
